import React, { useEffect, useState } from 'react';
import Chart from "react-apexcharts";
import { fetchRecordByKey } from "../../services/IndexedDBUtil";
import { DATABASE_NAME, STORE_NAME } from '../../services/constants'
import { displayMessage } from '../../Utils/helper';
import { getCookie } from '../../services/DataRequestService';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';

export default function Report1({ Product, Location }) {

  const [responseData, setResponseData] = useState([]);
  const [plotData, setPlotData] = useState([])
  const [barChartData, setBarChartData] = useState([])
  const [options, setOptions] = useState({})
  const [showChart, setShowChart] = useState(false);
  const [chartType, setChartType] = useState('line'); // 'line' or 'bar'

  useEffect(() => {
    const init = async () => {
      let indexedDBResponse = await fetchRecordByKey(DATABASE_NAME, STORE_NAME, 'WFActualPredictedData');
      if (indexedDBResponse?.success) {
        let _data = indexedDBResponse.record?.data
        let skuData = _data.cities.find(cityData => cityData.city === Location)?.data.find(skuData => skuData.sku === Product)?.data;
        setResponseData(skuData);
        DrawReport(skuData)
        PrepareBarChartData(skuData)
        setShowChart(true);
      }
    }
    try {
      if (!Product || !Location) {
        setShowChart(false);
      } else {
        init();
      }
    } catch (err) {
      setShowChart(false);
    }


  }, [Product, Location])
  function DrawReport(skuData) {
    // Build Actual series (blue)
    const actualSeries = {
      id: "Actual",
      color: "#0f1654", // very dark blue from theme
      data: skuData.map((item) => ({
        x: item.frequency_year,
        y: item.actual,
      })),
    };

    // Build Predicted series (orange)
    const predictedData = skuData.map((item) => ({
      x: item.frequency_year,
      y: item.predicted,
    }));

    const cutoff = predictedData.length - 3;

    // Normal predicted (orange)
    const predictedNormal = {
      id: "Predicted",
      color: "#2EB8AA", // darker teal from theme
      data: predictedData.slice(0, cutoff),
    };

    // Highlighted predicted (lighter orange, includes the point before cutoff for continuity)
    const predictedHighlighted = {
      id: "Forecasted", // 👈 internal ID, we’ll hide it from legend
      color: "#F2994A", // orange for forecasted
      data: predictedData.slice(cutoff - 1),
    };

    const modifiedData = [actualSeries, predictedNormal, predictedHighlighted];

    setPlotData(modifiedData);
  }

  // Prepare bar chart data with blue shades
  const PrepareBarChartData = (skuData) => {
    if (!skuData || skuData.length === 0) return;

    // Use all data points and separate actual/predicted/forecasted like line chart
    const cutoff = skuData.length - 3; // Last 3 are forecasted
    
    const barData = skuData.map((item, index) => ({
      period: item.frequency_year,
      Actual: item.actual || 0,
      Predicted: index < cutoff ? (item.predicted || 0) : 0, // Historical predicted
      Forecasted: index >= cutoff ? (item.predicted || 0) : 0, // Future forecasted
    }));

    setBarChartData(barData);
  }

  return (
    <>
    {showChart &&
    <div className="bg-white rounded-xl shadow-lg p-6">
      {/* Header with Chart Toggle */}
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-4xl font-bold text-[#0F1116]">Actual vs Predicted Analysis - {Product}</h3>
        <div className="flex space-x-2">
          <button
            onClick={() => setChartType('line')}
            className={`px-4 py-2 rounded-lg font-semibold transition-all ${
              chartType === 'line' 
                ? 'bg-[#1d2b7a] text-white shadow-lg' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Line Chart
          </button>
          {/* <button
            onClick={() => setChartType('bar')}
            className={`px-4 py-2 rounded-lg font-semibold transition-all ${
              chartType === 'bar' 
                ? 'bg-[#1d2b7a] text-white shadow-lg' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Bar Chart
          </button> */}
        </div>
      </div>

      {/* Chart Container with Noble Blue Border */}
      <div
        className="rounded-lg p-4"
        style={{ 
          border: "2px solid #1F4280", 
          height: "500px", 
          width: "100%",
          backgroundColor: "#f8fdf9"
        }}
      >
        {chartType === 'line' ? (
          <ResponsiveLine
            data={plotData}
            margin={{ top: 50, right: 40, bottom: 100, left: 70 }}
            xScale={{ type: "point" }}
            yScale={{ type: "linear", min: "auto", max: "auto", stacked: false }}
            colors={{ datum: "color" }}
            axisBottom={{
              orient: "bottom",
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
            }}
            useMesh={true}
            pointSize={8}
            legends={[
              {
                anchor: "bottom",
                direction: "row",
                translateY: 85,
                itemWidth: 160,
                itemHeight: 30,
                itemsSpacing: 60,
                symbolSize: 18,
                symbolShape: "circle",
                filter: (legend) => legend.id !== "Predicted (future)",
                itemTextColor: '#222',
                itemDirection: 'left-to-right',
                effects: [
                  {
                    on: 'hover',
                    style: {
                      itemTextColor: '#1d2b7a',
                      itemBackground: 'rgba(0,0,0,0.05)',
                    }
                  }
                ]
              },
            ]}
            theme={{
              legends: {
                text: {
                  fontSize: 19,
                  fontWeight: 700,
                  fill: '#222',
                }
              }
            }}
          />
        ) : (
          <ResponsiveBar
            data={barChartData}
            keys={['Actual', 'Predicted', 'Forecasted']}
            indexBy="period"
            margin={{ top: 60, right: 150, bottom: 120, left: 100 }}
            padding={0.3}
            groupMode="grouped"
            valueScale={{ type: 'linear' }}
            indexScale={{ type: 'band', round: true }}
            colors={['#0f1654', '#2EB8AA', '#F2994A']}
            borderColor={{
              from: 'color',
              modifiers: [['darker', 0.2]]
            }}
            borderWidth={1}
            borderRadius={2}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 0,
              tickPadding: 15,
              tickRotation: -45,
              legend: 'Time Period',
              legendPosition: 'middle',
              legendOffset: 100,
              format: value => value
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 15,
              tickRotation: 0,
              legend: 'Values',
              legendPosition: 'middle',
              legendOffset: -80,
              format: value => `${Math.round(value)}`
            }}
            enableLabel={false}
            enableGridX={false}
            enableGridY={true}
            gridYValues={8}
            theme={{
              background: '#ffffff',
              axis: {
                domain: {
                  line: {
                    stroke: 'transparent'
                  }
                },
                ticks: {
                  line: {
                    stroke: 'transparent'
                  },
                  text: {
                    fontSize: 16,
                    fill: '#666666',
                    fontWeight: 600
                  }
                },
                legend: {
                  text: {
                    fontSize: 20,
                    fontWeight: 700,
                    fill: '#1d2b7a'
                  }
                }
              },
              grid: {
                line: {
                  stroke: '#E0E0E0',
                  strokeWidth: 1,
                  strokeDasharray: '3 3'
                }
              },
              legends: {
                text: {
                  fontSize: 17,
                  fontWeight: 700,
                  fill: '#222'
                }
              }
            }}
            legends={[
              {
                dataFrom: 'keys',
                anchor: 'top-right',
                direction: 'column',
                justify: false,
                translateX: 160,
                translateY: 0,
                itemsSpacing: 25,
                itemWidth: 140,
                itemHeight: 30,
                itemDirection: 'left-to-right',
                itemOpacity: 1,
                symbolSize: 20,
                symbolShape: 'square',
                symbolBorderColor: 'rgba(0, 0, 0, .3)',
                effects: [
                  {
                    on: 'hover',
                    style: {
                      itemBackground: 'rgba(0, 0, 0, .05)',
                      itemOpacity: 1
                    }
                  }
                ]
              }
            ]}
          />
        )}
      </div>
    </div>}
    </>
  )
}